"use client";
import { useState, useEffect } from "react";
import { ChevronRight, Sparkles, Pi, Eye, Mail, Lock, ArrowRight, ShieldCheck, X } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import dreamWorldProvider from "../DreamWorldProvider";
import { setUserGlobalData } from "../store/dreamworldSlice";
import { isDreamLive } from "../utils/isDreamLive";

export default function LoginPage() {
  const [animate, setAnimate] = useState(false);
  const [piLoading, setPiLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredImage, setHoveredImage] = useState<number | null>(null);

  // Email login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const isDreamLiveDomain = isDreamLive();

  useEffect(() => {
    setAnimate(true);
  }, []);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const navigateAfterAuth = () => {
    const isNewUser = localStorage.getItem("isNewUser");
    if (isNewUser === null || isNewUser === "true") {
      navigate("/onboarding");
    } else {
      navigate("/home");
    }
  };

  const handleLogin = async () => {
    setPiLoading(true);
    setError(null);
    try {
      const userGlobalData = await dreamWorldProvider.signInWithPi();
      dispatch(setUserGlobalData(userGlobalData));
      localStorage.setItem("isSignedIn", "true");
      navigateAfterAuth();
    } catch (err: any) {
      setError(err.message || "Failed to sign in.");
    } finally {
      setPiLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    setError(null);
    try {
      const userGlobalData = await dreamWorldProvider.signInWithPi(true);
      dispatch(setUserGlobalData(userGlobalData));
      // localStorage.setItem("isSignedIn", "true");
      navigateAfterAuth();
    } catch (err: any) {
      setError(err.message || "Failed to access demo.");
    } finally {
      setDemoLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setEmailLoading(true);
    setError(null);
    try {
      const userGlobalData = await dreamWorldProvider.signInWithEmail(email, password);
      dispatch(setUserGlobalData(userGlobalData));
      localStorage.setItem("isSignedIn", "true");

      if (!userGlobalData.user.emailVerified) {
        setShowVerificationPrompt(true);
      } else {
        navigateAfterAuth();
      }
    } catch (err: any) {
      setError(err.message || "Failed to sign in.");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSendVerification = async () => {
    try {
      await dreamWorldProvider.sendVerificationEmail();
      setVerificationSent(true);
    } catch (err: any) {
      setError("Failed to send verification email. Try again.");
    }
  };

  const handleSkipVerification = () => {
    setShowVerificationPrompt(false);
    navigateAfterAuth();
  };

  const images = [
    {
      src: "https://images.pexels.com/photos/33710182/pexels-photo-33710182.png",
      alt: "Digital garden",
    },
  ];

  return (
    <div className="flex min-h-screen max-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-gradient-to-r from-pink-500/20 to-orange-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl animate-ping delay-2000"></div>
      </div>

      {/* Creative Visual Section */}
      <div className="hidden lg:flex lg:w-2/5 xl:w-1/2 p-6 relative z-10 max-h-screen overflow-hidden">
        <div className="w-full h-full">
          {images.length === 1 ? (
            // Single image fills entire space
            <div
              className="w-full h-full rounded-2xl overflow-hidden relative group cursor-pointer transform transition-all duration-500 ease-out hover:scale-[1.02] hover:rotate-1"
              onMouseEnter={() => setHoveredImage(0)}
              onMouseLeave={() => setHoveredImage(null)}
              style={{
                animation: animate ? "fadeInUp 0.8s ease-out forwards" : "none",
                opacity: animate ? 1 : 0,
                transform: animate ? "translateY(0)" : "translateY(20px)",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20"></div>
              <div className="absolute inset-0 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
              <img
                src={images[0].src}
                alt={images[0].alt}
                className={`w-full h-full object-cover transition-all duration-700 ease-out ${
                  hoveredImage === 0
                    ? "scale-110 brightness-110 saturate-125"
                    : "scale-100"
                }`}
              />
              {/* Glassmorphism overlay on hover */}
              <div
                className={`absolute inset-0 bg-gradient-to-t from-white/20 to-transparent backdrop-blur-sm rounded-2xl transition-all duration-500 ${
                  hoveredImage === 0 ? "opacity-100" : "opacity-0"
                }`}
              ></div>
              {/* Sparkle effect */}
              <div
                className={`absolute top-6 right-6 transition-all duration-300 ${
                  hoveredImage === 0
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-75"
                }`}
              >
                <Sparkles size={24} className="text-white/80" />
              </div>
            </div>
          ) : (
            // Multiple images in columns layout
            <div className="columns-4 gap-3 h-screen">
              {images.map((image, index) => (
                <div
                  key={index}
                  className="rounded-2xl overflow-hidden h-1000 mb-3 break-inside-avoid relative group cursor-pointer transform transition-all duration-500 ease-out hover:scale-105 hover:rotate-1"
                  onMouseEnter={() => setHoveredImage(index)}
                  onMouseLeave={() => setHoveredImage(null)}
                  style={{
                    animationDelay: `${index * 50}ms`,
                    animation: animate
                      ? "fadeInUp 0.8s ease-out forwards"
                      : "none",
                    opacity: animate ? 1 : 0,
                    transform: animate ? "translateY(0)" : "translateY(20px)",
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20"></div>
                  <div className="absolute inset-0 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                  <img
                    src={image.src}
                    alt={image.alt}
                    className={`w-full h-full object-cover transition-all duration-700 ease-out ${
                      hoveredImage === index
                        ? "scale-110 brightness-110 saturate-125"
                        : "scale-100"
                    }`}
                  />
                  {/* Glassmorphism overlay on hover */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-t from-white/20 to-transparent backdrop-blur-sm rounded-2xl transition-all duration-500 ${
                      hoveredImage === index ? "opacity-100" : "opacity-0"
                    }`}
                  ></div>
                  {/* Sparkle effect */}
                  <div
                    className={`absolute top-3 right-3 transition-all duration-300 ${
                      hoveredImage === index
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-75"
                    }`}
                  >
                    <Sparkles size={14} className="text-white/80" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Login Section */}
      <div className="flex flex-1 lg:w-3/5 xl:w-1/2 relative z-10 max-h-screen overflow-hidden">
        <div className="flex flex-col justify-center w-full px-6 py-8 lg:px-8 max-h-screen overflow-hidden">
          {/* Glassmorphism container */}
          <div className="backdrop-blur-2xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
            <div className="mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mr-4 shadow-lg backdrop-blur-sm">
                  <Sparkles size={24} className="text-white" />
                </div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-indigo-200 bg-clip-text text-transparent">
                  DreamWorld
                </h1>
              </div>
            </div>

            <div
              className={`transition-all duration-700 ease-out ${animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            >
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-white mb-4 text-balance leading-tight">
                  Reimagine your images
                </h2>
                <p className="text-white/70 text-lg leading-relaxed">
                  An all in one content creation tool
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-400/30 rounded-2xl backdrop-blur-sm">
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              {/* Verification Prompt Modal */}
              {showVerificationPrompt && (
                <div className="mb-6 p-5 bg-indigo-500/15 border border-indigo-400/30 rounded-2xl backdrop-blur-sm relative">
                  <button
                    onClick={handleSkipVerification}
                    className="absolute top-3 right-3 text-white/50 hover:text-white/80 transition-colors"
                  >
                    <X size={16} />
                  </button>
                  <div className="flex items-center mb-3">
                    <ShieldCheck size={20} className="text-indigo-300 mr-2" />
                    <h3 className="text-white font-semibold text-sm">Verify your email</h3>
                  </div>
                  <p className="text-white/60 text-xs mb-4 leading-relaxed">
                    Verify your email for extra security. You can also skip this step and do it later.
                  </p>
                  <div className="flex gap-3">
                    {!verificationSent ? (
                      <button
                        onClick={handleSendVerification}
                        className="flex-1 h-10 text-xs font-semibold bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-400 hover:to-purple-500 transition-all duration-300"
                      >
                        Send Verification Email
                      </button>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center text-xs text-center py-3 bg-emerald-500/15 border border-emerald-400/30 rounded-xl px-3">
                        <span className="font-semibold text-emerald-300 mb-1">✓ Verification email sent!</span>
                        <span className="text-white/50 leading-relaxed">
                          Check your inbox and click the verification link. Don't see it? Check your spam folder too.
                        </span>
                      </div>
                    )}
                    <button
                      onClick={handleSkipVerification}
                      className="h-10 px-5 text-xs font-medium text-white/70 hover:text-white bg-white/10 hover:bg-white/15 rounded-xl border border-white/20 transition-all duration-300 self-start"
                    >
                      Skip
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Email/Password Login — only on dreamm.live */}
                {isDreamLiveDomain && !showVerificationPrompt && (
                  <form onSubmit={handleEmailLogin} className="space-y-3">
                    {/* Email Input */}
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">
                        <Mail size={18} />
                      </div>
                      <input
                        id="email-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email address"
                        autoComplete="email"
                        className="w-full h-14 pl-12 pr-4 text-sm text-white placeholder-white/40 bg-white/10 border border-white/20 rounded-2xl focus:outline-none focus:border-indigo-400/60 focus:bg-white/15 backdrop-blur-sm transition-all duration-300"
                      />
                    </div>

                    {/* Password Input */}
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">
                        <Lock size={18} />
                      </div>
                      <input
                        id="password-input"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password (min 6 characters)"
                        autoComplete="current-password"
                        className="w-full h-14 pl-12 pr-12 text-sm text-white placeholder-white/40 bg-white/10 border border-white/20 rounded-2xl focus:outline-none focus:border-indigo-400/60 focus:bg-white/15 backdrop-blur-sm transition-all duration-300"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                      >
                        <Eye size={18} />
                      </button>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={emailLoading}
                      className="group relative w-full h-14 text-base font-semibold bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 hover:from-indigo-400 hover:via-purple-500 hover:to-pink-400 text-white rounded-2xl shadow-2xl hover:shadow-indigo-500/25 transition-all duration-500 disabled:opacity-50 backdrop-blur-sm border border-white/20 overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <div className="relative flex items-center justify-center">
                        {emailLoading ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                            <span>Signing in...</span>
                          </>
                        ) : (
                          <>
                            <span className="group-hover:translate-x-1 transition-transform duration-300">
                              Continue with Email
                            </span>
                            <ArrowRight
                              size={20}
                              className="ml-3 group-hover:translate-x-1 transition-transform duration-300"
                            />
                          </>
                        )}
                      </div>
                    </button>

                    <p className="text-center text-white/40 text-xs mt-2">
                      New user? An account will be created automatically.
                    </p>
                  </form>
                )}

                {/* Pi Network Login Button — hidden on dreamm.live */}
                {!isDreamLiveDomain && (
                  <button
                    onClick={handleLogin}
                    disabled={piLoading || demoLoading}
                    className="group relative w-full h-16 text-base font-semibold bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 hover:from-indigo-400 hover:via-purple-500 hover:to-pink-400 text-white rounded-2xl shadow-2xl hover:shadow-indigo-500/25 transition-all duration-500 disabled:opacity-50 backdrop-blur-sm border border-white/20 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    <div className="relative flex items-center justify-center">
                      {piLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                          <span>Connecting...</span>
                        </>
                      ) : (
                        <>
                          <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center mr-3 backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
                            <Pi
                              size={18}
                              className="group-hover:rotate-12 transition-transform duration-300"
                            />
                          </div>
                          <span className="group-hover:translate-x-1 transition-transform duration-300">
                            Continue with Pi Network
                          </span>
                          <ChevronRight
                            size={20}
                            className="ml-3 group-hover:translate-x-1 transition-transform duration-300"
                          />
                        </>
                      )}
                    </div>
                  </button>
                )}

                {/* Demo Account Button */}
                <button
                  onClick={handleDemoLogin}
                  disabled={piLoading || demoLoading || emailLoading}
                  className="group relative w-full h-14 text-base font-medium bg-white/10 hover:bg-white/20 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 disabled:opacity-50 backdrop-blur-xl border border-white/30 hover:border-white/40 overflow-hidden"
                >
                  {/* Subtle shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>

                  <div className="relative flex items-center justify-center">
                    {demoLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center mr-3 backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
                          <Eye
                            size={14}
                            className="group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        <span className="group-hover:translate-x-0.5 transition-transform duration-300">
                          Explore with Demo Account
                        </span>
                        <ChevronRight
                          size={18}
                          className="ml-2 group-hover:translate-x-1 transition-transform duration-300"
                        />
                      </>
                    )}
                  </div>
                </button>

                {/* Features */}
                <div className="pt-8 space-y-4">
                  {[
                    {
                      color: "bg-emerald-400",
                      text: isDreamLiveDomain ? "Secure email authentication" : "Secure blockchain authentication",
                    },
                    {
                      color: "bg-blue-400",
                      text: "Cutting-edge AI models for creators",
                    },
                    { color: "bg-purple-400", text: "Privacy-first design" },
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center text-sm text-white/80 group cursor-default"
                      style={{
                        animationDelay: `${(index + 1) * 200}ms`,
                        animation: animate
                          ? "fadeInLeft 0.6s ease-out forwards"
                          : "none",
                        opacity: animate ? 1 : 0,
                      }}
                    >
                      <div
                        className={`w-2.5 h-2.5 ${feature.color} rounded-full mr-4 group-hover:scale-125 group-hover:shadow-lg transition-all duration-300`}
                      ></div>
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-12 pt-6 border-t border-white/20">
              <p className="text-xs text-white/60 text-center leading-relaxed">
                By continuing, you agree to our{" "}
                <a
                  href="#"
                  className="text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="#"
                  className="text-indigo-300 hover:text-indigo-200 hover:underline transition-colors duration-200"
                >
                  Privacy Policy
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Enhanced glassmorphism effects */
        .backdrop-blur-2xl {
          backdrop-filter: blur(40px);
        }

        .backdrop-blur-xl {
          backdrop-filter: blur(24px);
        }

        /* Custom shadow effects */
        .shadow-2xl {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }
      `}</style>
    </div>
  );
}
